import { FilePlus2, FileUp, LoaderCircle, X } from "lucide-react";
import { useNavigate } from "react-router";
import { useMemo, useState } from "react";
import { createBlankWorkbook } from "@/lib/api/endpoints";
import { setPendingUpload } from "@/lib/import/pending-upload";
import type {
  CreateBlankWorkbookRequest,
  CreateBlankWorkbookResponse,
  ProfileSummary,
} from "@/lib/api/types";

export type NewWorkbookMode = "blank" | "template" | "upload";

export type NewWorkbookTemplate = Pick<
  ProfileSummary,
  "id" | "name" | "productType" | "type" | "isDefault"
>;

export type NewWorkbookSelection = {
  mode: NewWorkbookMode;
  name?: string;
  templateId?: string | null;
};

type Props = {
  profiles: NewWorkbookTemplate[];
  triggerLabel?: string;
  triggerClassName?: string;
};

function sortTemplates(
  profiles: readonly NewWorkbookTemplate[],
): NewWorkbookTemplate[] {
  return [...profiles].sort((a, b) => {
    if (a.productType !== b.productType) {
      return a.productType.localeCompare(b.productType);
    }
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

export function newWorkbookRequestFromSelection(
  selection: NewWorkbookSelection,
): CreateBlankWorkbookRequest {
  const request: CreateBlankWorkbookRequest = {};
  const name = selection.name?.trim();
  if (name) request.name = name;
  if (selection.mode === "template" && selection.templateId) {
    request.templateId = selection.templateId;
  }
  return request;
}

/** Blank workbooks are created with a single sheet named "Sheet1" (backend). */
const BLANK_WORKBOOK_SHEET = "Sheet1";

export async function submitNewWorkbookDialog(args: {
  selection: NewWorkbookSelection;
  navigate: (href: string) => void;
}): Promise<CreateBlankWorkbookResponse> {
  const workbook = await createBlankWorkbook(
    newWorkbookRequestFromSelection(args.selection),
  );
  // Land directly in the spreadsheet editor (the same UI as opening a workbook),
  // not the separate overview page.
  args.navigate(
    `/workbooks/${workbook.id}/sheets/${encodeURIComponent(BLANK_WORKBOOK_SHEET)}`,
  );
  return workbook;
}

export function NewWorkbookDialog({
  profiles,
  triggerLabel = "New workbook",
  triggerClassName,
}: Props) {
  const navigate = useNavigate();
  const templates = useMemo(() => sortTemplates(profiles), [profiles]);
  const firstTemplateId = templates[0]?.id ?? "";

  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<NewWorkbookMode>("blank");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState(firstTemplateId);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const canUseTemplate = templates.length > 0;
  const selectedTemplateId = templateId || firstTemplateId;
  const canSubmit =
    !submitting &&
    (mode === "blank" ||
      (mode === "template" && canUseTemplate && !!selectedTemplateId) ||
      (mode === "upload" && !!uploadFile));

  function close() {
    if (submitting) return;
    setOpen(false);
    setError(null);
    setUploadFile(null);
  }

  async function handleSubmit() {
    if (!canSubmit) return;

    // Upload mode: store file in pending-upload singleton then navigate to the
    // dedicated header-configurator page (instead of opening the modal wizard).
    if (mode === "upload") {
      if (!uploadFile) {
        setError("Choose a CSV or Excel file first.");
        return;
      }
      setPendingUpload({ file: uploadFile });
      setOpen(false);
      navigate("/workbooks/import");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await submitNewWorkbookDialog({
        selection: {
          mode,
          name,
          templateId: mode === "template" ? selectedTemplateId : null,
        },
        navigate: (href: string) => navigate(href),
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not create workbook",
      );
      setSubmitting(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          triggerClassName ??
          "inline-flex w-fit items-center gap-2 rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
        }
      >
        <FilePlus2 className="h-4 w-4" />
        {triggerLabel}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-workbook-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6 backdrop-blur-sm"
        >
          <button
            type="button"
            aria-label="Close new workbook dialog"
            className="absolute inset-0 cursor-default"
            onClick={close}
          />
          <div className="relative w-full max-w-lg overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-border">
              <div>
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Workbooks
                </p>
                <h2
                  id="new-workbook-title"
                  className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100"
                >
                  New workbook
                </h2>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-surface-2 dark:hover:text-slate-100"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              <label className="block">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                  Name
                </span>
                <input
                  value={name}
                  onChange={(event) => setName(event.currentTarget.value)}
                  placeholder="Untitled workbook"
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
                />
              </label>

              <div className="grid gap-2 sm:grid-cols-3" role="group">
                <button
                  type="button"
                  onClick={() => setMode("blank")}
                  aria-pressed={mode === "blank"}
                  className={[
                    "rounded-md border px-3 py-3 text-left transition",
                    mode === "blank"
                      ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold">Blank</span>
                  <span className="mt-1 block text-xs opacity-75">
                    Start with editable rows.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setMode("template");
                    if (!templateId && firstTemplateId) {
                      setTemplateId(firstTemplateId);
                    }
                  }}
                  disabled={!canUseTemplate}
                  aria-pressed={mode === "template"}
                  className={[
                    "rounded-md border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60",
                    mode === "template"
                      ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold">
                    From template
                  </span>
                  <span className="mt-1 block text-xs opacity-75">
                    Use an output field set.
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setMode("upload")}
                  aria-pressed={mode === "upload"}
                  className={[
                    "rounded-md border px-3 py-3 text-left transition",
                    mode === "upload"
                      ? "border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-950"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="block text-sm font-semibold">Upload file</span>
                  <span className="mt-1 block text-xs opacity-75">
                    Import a CSV or Excel workbook.
                  </span>
                </button>
              </div>

              {mode === "template" && (
                <label className="block">
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    Template
                  </span>
                  <select
                    value={selectedTemplateId}
                    onChange={(event) => setTemplateId(event.currentTarget.value)}
                    disabled={!canUseTemplate}
                    className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:opacity-60 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
                  >
                    {templates.map((profile) => (
                      <option key={profile.id} value={profile.id}>
                        {profile.name} - {profile.productType}
                        {profile.isDefault ? " (default)" : ""}
                      </option>
                    ))}
                  </select>
                  {!canUseTemplate && (
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                      No templates are available for the active customer.
                    </p>
                  )}
                </label>
              )}

              {mode === "upload" && (
                <label className="block rounded-[10px] border border-dashed border-slate-300 bg-slate-50 px-4 py-4 transition hover:border-slate-400 dark:border-border dark:bg-background/60 dark:hover:border-slate-600">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-slate-200">
                    <FileUp className="h-4 w-4" />
                    Source file
                  </span>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    disabled={submitting}
                    onChange={(event) => {
                      const next = event.currentTarget.files?.[0] ?? null;
                      setUploadFile(next);
                      setError(null);
                    }}
                    className="mt-3 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-700 disabled:cursor-not-allowed dark:text-slate-300 dark:file:bg-slate-100 dark:file:text-slate-950 dark:hover:file:bg-slate-200"
                  />
                  <span className="mt-2 block truncate text-xs text-slate-500 dark:text-slate-400">
                    {uploadFile ? uploadFile.name : "CSV, XLSX, or XLS"}
                  </span>
                </label>
              )}

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300"
                >
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4 dark:border-border">
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
              >
                {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
                {mode === "upload" ? "Next" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
}
